import React from 'react'
import { intl } from 'lang/intl'

import {
  TtButton,
  TtIcon,
  TtTrayChyronSpeakersOverview,
  TtTraySpeakerHolder,
} from '@23/react'

import {
  useOverlayState,
  setChyron,
  removeOverlay,
} from '../../../../firebase/hooks/useOverlays'
import { ChyronType } from '../../../../firebase/model/chyron-model'
import {
  setActiveCard,
  useTrayCard,
  useTrayCardState,
} from '../../../../firebase/hooks/useTray'
import { useVisibleWebinarSpeakers } from '../../../../sharedLegacy/hooks/useSpeakersQuery'
import { type PropsWithTrayItemChildren } from '../TrayItem'

interface ChyronSpeakersOverviewCardProps {
  id: string
}

const ChyronSpeakersOverviewCard = ({
  id,
  children,
}: PropsWithTrayItemChildren<ChyronSpeakersOverviewCardProps>) => {
  const card = useTrayCard(id)
  const cardState = useTrayCardState(id)
  const overlay = useOverlayState()
  const speakers = useVisibleWebinarSpeakers()
  const isChyronShowing = overlay?.type === ChyronType.SpeakersOverview

  const showChyron = () => {
    if (!isChyronShowing) {
      void setChyron({
        type: ChyronType.SpeakersOverview,
        speakers: speakers.map((speaker) => {
          const name = speaker.fullName
          const { title, avatarURL, company } = speaker
          return { name, title, avatarUrl: avatarURL, company }
        }),
      })
    }
  }

  const onCardClickHandler = () => {
    if (speakers.length > 0) showChyron()
    setActiveCard(id)
  }

  if (card?.type !== 'chyron-speakers-overview') return null

  return (
    <TtTrayChyronSpeakersOverview
      tag={intl.formatMessage({ defaultMessage: 'Speakers' })}
      selected={cardState === 'active'}
      disabled={cardState === 'done'}
      onTt-click={(event) => {
        if (event.target === event.currentTarget) {
          onCardClickHandler()
        }
      }}
    >
      <TtButton
        slot="toolbar"
        label={
          isChyronShowing
            ? intl.formatMessage({
                defaultMessage: 'Remove the speakers overview TvCard',
              })
            : intl.formatMessage({
                defaultMessage: 'Display speakers overview TvCard',
              })
        }
        color="secondary"
        variant="ghost"
        size="x-small"
        circle
        onClick={isChyronShowing ? removeOverlay : showChyron}
      >
        <TtIcon
          library="one/webinars"
          name={isChyronShowing ? 'visibility-off' : 'visibility-on'}
        />
      </TtButton>
      {children?.()}
      {speakers.map((speaker) => (
        <TtTraySpeakerHolder
          key={speaker.id}
          name={speaker.fullName}
          company={speaker.company}
          jobTitle={speaker.title}
          imageUrl={speaker.avatarURL}
        />
      ))}
    </TtTrayChyronSpeakersOverview>
  )
}

export { ChyronSpeakersOverviewCard }
